// Mail→Vorgang SP2 — pure, DB-/DOM-freie State-Machine der Command-Leiste im
// Mail-Lesen (MailDetailContent). Bewusst aus der Komponente herausgelöst,
// damit die Zustands-Ableitung in der Vitest-Suite testbar ist (Muster
// mailFilter.ts / mailSearch.ts).
//
// Drei States (Vorplanung §3.2, User-Entscheidungen 2026-07-05 #25/#26):
//   A „gehört-zu"      — die Konversation hat ≥1 MailLink mit auflösbarem
//                        Vorgang, an den DIESE Nachricht noch nicht gebunden
//                        ist → „Gehört zu diesem Vorgang" mit [Bestätigen]
//                        (ordnet zu UND archiviert, Entscheidung #25) und [X]
//                        (Vorschlag verwerfen → Fallback B/C). Bei mehreren
//                        Vorgängen Checkbox-Auswahl (einer/mehrere/alle).
//   B „erkannt"        — EIN erkannter Kontakt (01.11.2026: Rangfolge
//                        Betreff → Adresse → Domäne, bestimmt vom Server) als
//                        Button mit Typ-Badge (Firma/AP/Person); alle übrigen
//                        Kandidaten (Adressen, seit T-265 Kunden aus dem
//                        Betreff) stehen in „Andere" zur Auswahl, nicht als
//                        zweites „erkannt" daneben ({@link teileStateB}).
//                        Klick auf den Button öffnet die Zuweisung. Danach ein
//                        VERTIKALER Trenner (explizit kein „/") und „Neuer
//                        Vorgang".
//   C „nicht-erkannt"  — orange/gelbe (nicht rote) Warnung + „Kontakt
//                        zuweisen" (Platzhalter; der echte Anlage-Flow kommt
//                        mit SP4).
import type {
    MailContactRecognition,
    MailRecognitionOutcome,
    MailSenderCandidate,
} from "../../services/graphMailService";
import { KONTAKTTYP_PERSON_LABEL } from "../../utils/contactTypeLabel";

/** Aufgelöster Vorgang-Bezug eines Konversations-Links (Spiegel von `MailLinkVorgangRef`). */
export type MailCommandVorgangRef = {
    id: number;
    titel: string;
    /** Erledigte Vorgänge werden nicht mehr als Ziel vorgeschlagen (PO 28.09.2026). */
    erledigt?: boolean;
};

/**
 * Ein MailLink der Konversation, soweit die State-Ableitung ihn braucht —
 * strukturelles Subset von `MailLinkWithVorgangDto` (mailLinkService).
 * `vorgang: null` = nicht auflösbar (z. B. Todo soft-gelöscht) → zählt NICHT
 * als „gehört zu"-Vorschlag.
 */
export type ConversationLinkLike = {
    messageId: string;
    /** RFC-Id — bei der Kopie desselben Mails in einem anderen Postfach gleich. */
    internetMessageId?: string | null;
    vorgang?: MailCommandVorgangRef | null;
};

/**
 * Hängt dieser Link an DIESER Nachricht? Über die Graph-Id (dieselbe Kopie)
 * ODER die RFC-Id (Rücksprache 06.10.2026): Stehen mehrere im An/Cc/Bcc und
 * hat einer die Mail aus SEINEM Postfach zugewiesen, ist sie auch für die
 * anderen zugewiesen — markiert, nicht noch einmal vorgeschlagen.
 */
export function istDieseNachricht(
    l: Pick<ConversationLinkLike, "messageId" | "internetMessageId">,
    messageId: string,
    internetMessageId?: string | null,
): boolean {
    if (l.messageId === messageId) return true;
    const eigene = (internetMessageId ?? "").trim();
    return eigene.length > 0 && (l.internetMessageId ?? "").trim() === eigene;
}

export type MailCommandState =
    | { kind: "A"; vorgaenge: MailCommandVorgangRef[] }
    | {
          kind: "B";
          candidates: MailSenderCandidate[];
          /**
           * Vorausgewählter Kandidat — `null`, wenn es Chips gibt, aber keiner
           * stark genug für eine Vorauswahl ist (nur Domänen-Treffer oder nur
           * Empfänger-Treffer). Die Leiste zeigt die Chips dann ohne Auswahl:
           * der User entscheidet mit einem Klick.
           */
          defaultContactId: number | null;
      }
    | {
          /**
           * Cluster ERKENNUNG (A4) — „unbekannte Adresse an bekannter
           * Firmendomäne". Ein eigener Zustand, weil das eine ANDERE Aussage
           * ist als „Kontakt erkannt" und einen anderen Anschluss braucht
           * („neuen Ansprechpartner anlegen"). Es stehen bewusst KEINE
           * Kandidaten-Chips daneben — der Hinweis ist ein Angebot, keine
           * Zuordnung.
           */
          kind: "D";
          domain: string;
          address: string;
          companies: { contactId: number; displayName: string }[];
          ambiguous: boolean;
          /** Firmen, die wegen des Deckels nicht in `companies` stehen. */
          more: number;
      }
    | {
          kind: "C";
          /**
           * WARUM nichts erkannt wurde — die orange Warnleiste hängt daran.
           *  · `none`          — es gab externe Adressen, keine war zuordenbar.
           *  · `internal-only` — nur Kolleginnen und Kollegen beteiligt; eine
           *                      Meldung „Absender wurde nicht erkannt" wäre
           *                      hier sinnlos, der Zuweisen-Button bleibt.
           *  · `legacy`        — keine Server-Erkennung vorhanden (Antwort
           *                      fehlt, scheitert oder altes Backend) → das
           *                      heutige Verhalten inklusive Warnung.
           */
          reason: "none" | "internal-only" | "legacy";
      };

/** Sortier-Rang der Kandidaten-Typen (B2): Firma → AP → Person. */
const KIND_RANK: Record<MailSenderCandidate["kind"], number> = {
    firma: 0,
    ap: 1,
    person: 2,
};

/**
 * Sortier-Rang des Adressfelds (PO 26.07.2026): Absender vor An vor Cc.
 *
 * Fehlt `foundIn`, gilt `from` — so bleiben ältere gecachte Antworten, die das
 * Feld noch nicht tragen, an ihrem bisherigen Platz.
 */
const ORIGIN_RANK: Record<NonNullable<MailSenderCandidate["foundIn"]>, number> = {
    from: 0,
    to: 1,
    cc: 2,
};

const originRank = (c: MailSenderCandidate): number => ORIGIN_RANK[c.foundIn ?? "from"];

/** T-265 — ein Kunde aus dem Betreff: ein Angebot, nie eine Vorauswahl. */
export const istBetreffTreffer = (c: MailSenderCandidate): boolean => c.matchedBy === "betreff";

/** Betreff-Treffer stehen hinter allen Adress-Treffern (sie tragen kein `foundIn`). */
const betreffRank = (c: MailSenderCandidate): number => (istBetreffTreffer(c) ? 1 : 0);

/**
 * Kandidaten defensiv in die Soll-Reihenfolge bringen (stabil — das Backend
 * sortiert bereits, aber die Leiste verlässt sich nicht darauf) und nach
 * `contactId` deduplizieren.
 *
 * Primär nach Adressfeld (Absender → An → Cc), erst danach nach Typ
 * (Firma → AP → Person). Das Adressfeld wiegt schwerer: eine Firma, die nur im
 * Cc steht, ist ein schwächerer Hinweis als die Person, die die Mail
 * tatsächlich geschickt hat.
 */
export function sortSenderCandidates(candidates: MailSenderCandidate[]): MailSenderCandidate[] {
    const seen = new Set<number>();
    const unique = candidates.filter((c) => {
        if (seen.has(c.contactId)) return false;
        seen.add(c.contactId);
        return true;
    });
    // Stabiler Sort über Index-Tupel (Array.prototype.sort ist erst ab ES2019
    // garantiert stabil — hier explizit, TS-Target ist älter).
    return unique
        .map((c, i) => ({ c, i }))
        .sort(
            (a, b) =>
                betreffRank(a.c) - betreffRank(b.c) ||
                originRank(a.c) - originRank(b.c) ||
                KIND_RANK[a.c.kind] - KIND_RANK[b.c.kind] ||
                a.i - b.i,
        )
        .map((x) => x.c);
}

/**
 * Default-Selektion (B2): ein AP mit Firmenbezug wird als kombinierter Chip
 * (Firma + AP) gerendert und ist der primäre Vorschlag → dessen `contactId` ist
 * die Default-Auswahl. Sonst erste Firma, sonst der erste Kandidat.
 *
 * Vorgelagert seit PO 24.08.2026: Produktpartner (Versicherer) scheiden aus
 * — seit T-265 ausnahmslos, auch wenn sonst niemand da ist („Zuordnung nie zu
 * Versicherern automatisiert"). Ebenso nie ein Kunde aus dem Betreff: dort
 * wählt der Nutzer.
 */
export function defaultCandidateSelection(sorted: MailSenderCandidate[]): number | null {
    if (sorted.length === 0) return null;
    // Zwei Klassen von Kandidaten werden NICHT vorausgewählt, beide aus
    // demselben Grund: sie sind Hinweise, keine Aussagen.
    //
    // 1. Nur über die DOMÄNE gefunden — „gleiche Domäne, also dieselbe Firma"
    //    stimmt meistens, aber nicht immer.
    // 2. Nur als EMPFÄNGER gefunden (An/Cc, seit PO 26.07.2026) — eine Mail an
    //    einen Verteiler nennt beliebig viele Kontakte, und keiner davon ist
    //    dadurch der Gemeinte. Wer die Mail geschickt hat, ist die stärkere
    //    Aussage.
    //
    // Die Chips stehen in beiden Fällen da und sind einen Klick entfernt — die
    // Zuordnung bleibt eine Entscheidung.
    // Produktpartner sind nachrangig (PO 24.08.2026) — und zwar VOR dem
    // From-Filter, nicht darin. Sonst entstünde genau der Widerspruch, den die
    // Regel abschaffen soll: Schreibt der Versicherer und steht der Kunde im
    // An-Feld, wäre die nicht-nachrangige Teilmenge des From-Felds leer, und
    // die Vorauswahl fiele auf den Versicherer zurück — während Kopf und rechte
    // Leiste längst den Kunden zeigen, den der Server gewählt hat.
    //
    // Mit dem Filter davor bleibt in diesem Fall NICHTS vorausgewählt: Der
    // Kunde steht nur im An-Feld und wird nach der Regel unten nie
    // vorausgewählt, der Versicherer ist ausgeschieden. Das ist der ehrliche
    // Zustand — die Chips stehen da, die Zuordnung bleibt eine Entscheidung.
    // Bis T-265 gewann der Produktpartner wieder, wenn es GAR keinen anderen
    // gab; das ist vorbei.
    const basis = sorted.filter((c) => !c.istProduktpartner && !istBetreffTreffer(c));
    const exact = basis.filter((c) => c.matchedBy !== "domain" && (c.foundIn ?? "from") === "from");
    if (exact.length === 0) return null;
    const combinedAp = exact.find((c) => c.kind === "ap" && c.viaCompanyId != null);
    if (combinedAp) return combinedAp.contactId;
    const firma = exact.find((c) => c.kind === "firma");
    return (firma ?? exact[0]).contactId;
}

/**
 * Vorauswahl aus dem SERVERSEITIG erkannten Kontakt (`recognition.contact`,
 * Rangfolge Betreff → Adresse → Domäne). Damit zeigt die Leiste denselben
 * Kontakt als „erkannt" wie die rechte Leiste und das Kontextmenü der Liste —
 * vorher wählte die Leiste nach eigener Regel und stellte Betreff-Treffer als
 * zweites Angebot daneben.
 *
 * Ist der erkannte Kontakt die Firma eines Ansprechpartners, der die Mail
 * geschickt hat, wird der AP vorausgewählt: Er erscheint als kombinierter Chip
 * „Firma – AP" und wird über `resolveCandidateAssignment` ohnehin der Firma
 * zugewiesen (F1a). Steht der Kontakt nicht unter den Kandidaten → `null`.
 */
export function erkannteAuswahl(
    contactId: number,
    candidates: MailSenderCandidate[],
): number | null {
    const ap = candidates.find(
        (c) =>
            c.kind === "ap" &&
            c.viaCompanyId === contactId &&
            c.matchedBy !== "domain" &&
            (c.foundIn ?? "from") === "from",
    );
    if (ap) return ap.contactId;
    return candidates.some((c) => c.contactId === contactId) ? contactId : null;
}

/**
 * Vorgänge, die die Konversation bereits kennt und an die DIESE Nachricht noch
 * NICHT gebunden ist (State-A-Vorschläge). Dedupliziert nach Vorgang-id; die
 * Link-Reihenfolge (Backend: neueste zuerst) bleibt erhalten. Ist die
 * Nachricht selbst schon an einen Vorgang gebunden (direkt oder über ein
 * Todo), wird GENAU dieser Vorgang nicht erneut vorgeschlagen (Leitplanke L6:
 * kein Doppel-Bind auf messageId+vorgangId).
 */
export function suggestedVorgaenge(
    links: ConversationLinkLike[],
    messageId: string,
    internetMessageId?: string | null,
): MailCommandVorgangRef[] {
    const ownVorgangIds = new Set(
        links
            .filter((l) => istDieseNachricht(l, messageId, internetMessageId) && l.vorgang)
            .map((l) => l.vorgang!.id),
    );
    const out: MailCommandVorgangRef[] = [];
    const seen = new Set<number>();
    for (const link of links) {
        const v = link.vorgang;
        if (!v || seen.has(v.id) || ownVorgangIds.has(v.id)) continue;
        seen.add(v.id);
        // PO 28.09.2026: „keine Zuweisung in Mail zu erledigten Vorgängen
        // anzeigen" — ein erledigter Vorgang ist kein Vorschlag mehr.
        if (v.erledigt) continue;
        out.push(v);
    }
    return out;
}

/**
 * Die zentrale State-Ableitung: Links > 0 (mit auflösbarem, noch nicht
 * gebundenem Vorgang) → A; sonst Kandidaten > 0 → B; sonst C.
 * `dismissed` = der User hat den „Gehört zu"-Vorschlag per [X] verworfen →
 * A wird übersprungen (Fallback B/C).
 */
export function deriveMailCommandState(input: {
    messageId: string;
    /** RFC-Id der geöffneten Nachricht — erkennt die Zuweisung eines Kollegen. */
    internetMessageId?: string | null;
    links: ConversationLinkLike[];
    candidates: MailSenderCandidate[];
    dismissed: boolean;
    /**
     * Cluster ERKENNUNG — der Server-Zustand. AUSDRÜCKLICH OPTIONAL: Fehlt das
     * Feld oder ist es `null` (Antwort lädt noch, ist gescheitert, oder das
     * Backend kennt die Erkennung nicht), gilt der LEGACY-Pfad — exakt das
     * bisherige Verhalten, `C` bekommt dann `reason: "legacy"` und warnt wie
     * bisher.
     */
    recognition?: MailContactRecognition | null;
    /**
     * T-265 — Kunden aus dem Betreff (`recognition.subjectCandidates`). Sie
     * stehen hinter den Adress-Treffern und machen einen sonst leeren Verlauf
     * zu State B. Vorausgewählt ist einer nur, wenn der Server ihn als
     * `contact` erkannt hat (eindeutig im Betreff, Stufe 1 der Rangfolge).
     */
    subjectCandidates?: MailSenderCandidate[];
}): MailCommandState {
    if (!input.dismissed) {
        const vorgaenge = suggestedVorgaenge(input.links, input.messageId, input.internetMessageId);
        if (vorgaenge.length > 0) return { kind: "A", vorgaenge };
    }
    // T-265: Ein Versicherer ist nie ein Treffer. Das Backend liefert keinen
    // mehr; eine ältere gecachte Listen-Annotation könnte aber noch einen tragen.
    const sorted = sortSenderCandidates(input.candidates).filter(
        (c) => !c.istProduktpartner && !istBetreffTreffer(c),
    );
    const adressIds = new Set(sorted.map((c) => c.contactId));
    const betreff = (input.subjectCandidates ?? [])
        .filter((c) => !c.istProduktpartner && !adressIds.has(c.contactId))
        .map((c): MailSenderCandidate => ({ ...c, matchedBy: "betreff" }));
    // Der EINE erkannte Kontakt kommt vom Server (Rangfolge Betreff → Adresse
    // → Domäne). Nur ohne Server-Antwort gilt die eigene Vorauswahl der Leiste.
    const serverKontakt =
        input.recognition?.status === "contact" ? input.recognition.contact ?? null : null;
    const vorauswahl = (alle: MailSenderCandidate[]): number | null =>
        input.recognition
            ? serverKontakt
                ? erkannteAuswahl(serverKontakt.contactId, alle)
                : null
            : defaultCandidateSelection(sorted);
    if (sorted.length > 0) {
        // Kandidaten OHNE Vorauswahl sind trotzdem State B: sie werden als
        // Chips angeboten. Früher landete dieser Fall in C („nicht erkannt")
        // und die gefundenen Kontakte verschwanden ganz — seit Empfänger
        // mitzählen (PO 26.07.2026) wäre das der Normalfall bei ausgehenden
        // Mails gewesen.
        const alle = [...sorted, ...betreff];
        return { kind: "B", candidates: alle, defaultContactId: vorauswahl(alle) };
    }
    // Zustand D steht NACH B und das ist korrekt: Im Domänen-Fall liefert das
    // Backend garantiert keine Kandidaten (`includeDomainFallback: false`),
    // B kann D also nicht verdecken.
    const domain = input.recognition?.companyDomain;
    if (
        domain &&
        (input.recognition?.status === "company-domain" ||
            input.recognition?.status === "company-domain-ambiguous")
    ) {
        return {
            kind: "D",
            domain: domain.domain,
            address: domain.address,
            companies: domain.companies,
            ambiguous: domain.ambiguous,
            more: domain.more,
        };
    }
    // T-265: Nennt nur der Betreff einen Kunden (Mail an den Versicherer,
    // Kolleginnen unter sich), wird er angeboten — ohne Vorauswahl. Zustand D
    // geht vor: „neuer Ansprechpartner dieser Firma" ist die genauere Aussage.
    if (betreff.length > 0) {
        return { kind: "B", candidates: betreff, defaultContactId: vorauswahl(betreff) };
    }
    if (input.recognition?.status === "internal-only") {
        return { kind: "C", reason: "internal-only" };
    }
    if (input.recognition?.status === "none") return { kind: "C", reason: "none" };
    return { kind: "C", reason: "legacy" };
}

/**
 * Die orange Warnleiste „Absender wurde nicht erkannt".
 *
 * Nur, wenn im ganzen Verlauf wirklich nichts Zuweisbares existierte (`none`)
 * — oder wenn gar keine Server-Erkennung vorliegt (`legacy`), dann gilt das
 * bisherige Verhalten. Bei rein internen Beteiligten schweigt sie: dort war die
 * Meldung immer sinnlos.
 */
export function showUnrecognizedWarning(state: MailCommandState): boolean {
    return state.kind === "C" && (state.reason === "none" || state.reason === "legacy");
}

/**
 * Der prominente Button „Kontakt zuweisen".
 *
 * Steht in ALLEN C-Gründen — auch bei `internal-only`. Das ist der Kernfall
 * „zwei Kolleginnen schreiben über einen Kunden, ich muss die Mail zuweisen":
 * still, aber mit Ein-Klick-Button. Und im Zustand D, wo er die Firma anbietet.
 */
export function showAssignContactButton(state: MailCommandState): boolean {
    return state.kind === "D" || state.kind === "C";
}

/**
 * Der neutrale „Neuer Vorgang"-Button. Ohne diesen dritten Helfer stünden im
 * Zustand D „Neuer Vorgang" UND „Kontakt zuweisen" nebeneinander.
 */
export function showNeutralVorgangButton(state: MailCommandState): boolean {
    return state.kind !== "B" && !showAssignContactButton(state);
}

/**
 * Cluster ERKENNUNG (A2) — DIE Auswahlregel des angezeigten Kontakts.
 *
 * Eine Quelle, eine Funktion: Liegt die Erkennungsantwort vor, ist ihr
 * `contact` maßgeblich — auch wenn er `null` ist (z. B. im Domänen-Fall). Es
 * wird dann NICHT auf die Listen-Annotation zurückgefallen, sonst stünden zwei
 * Wahrheiten nebeneinander.
 *
 * Nur solange die Antwort fehlt, lädt oder gescheitert ist, gilt die
 * Listen-Annotation `recognizedContact` (Listenobjekt vor Detail). Auf
 * `matchedContact` wird NIE zurückgefallen — dort steht auch ein interner
 * Kollege, und genau den soll A2 aus der Anzeige heraushalten.
 */
export function pickDisplayContact(
    outcome: MailRecognitionOutcome | { status: "loading" } | null | undefined,
    listItem: { recognizedContact?: { id: number; displayName: string } | null } | null | undefined,
    detail: { recognizedContact?: { id: number; displayName: string } | null } | null | undefined,
): { id: number; displayName: string } | null {
    if (outcome && outcome.status === "ok" && outcome.data.contactRecognition) {
        const contact = outcome.data.contactRecognition.contact;
        return contact ? { id: contact.contactId, displayName: contact.displayName } : null;
    }
    return listItem?.recognizedContact ?? detail?.recognizedContact ?? null;
}

/**
 * Cluster ZUWEISEN (B5) — die WIRKSAME Zuweisung einer Mail.
 *
 * Es gibt zwei Quellen und eine feste Rangfolge:
 *   1. die EXPLIZITE Zuweisung aus dem Zuweisen-Popup (der Nutzer hat
 *      entschieden) — sie gewinnt immer;
 *   2. sonst der ausgewählte Erkennungs-Kandidat, über
 *      `resolveCandidateAssignment` auf die Firma umgelegt, wenn er ein
 *      Ansprechpartner ist.
 *
 * Eine Auswahl, die es nach einem Neuladen nicht mehr gibt, zählt nicht mehr —
 * dann greift wieder der Default. Diese Funktion ist der gemeinsame Vertrag mit
 * der rechten Leiste (Cluster SEITENLEISTE): sie liest die Zuweisung hier und
 * nicht `matchedContact`, sonst zeigte sie nach einer Änderung den alten Kontakt.
 */
export function effektiveZuweisung(
    zuweisung: { id: number; displayName: string } | null,
    ausgewaehlt: MailSenderCandidate | null,
    candidates: MailSenderCandidate[],
): { id: number; displayName: string } | null {
    if (zuweisung) return zuweisung;
    if (!ausgewaehlt) return null;
    if (!candidates.some((c) => c.contactId === ausgewaehlt.contactId)) return null;
    return resolveCandidateAssignment(ausgewaehlt, candidates);
}

/**
 * Badge-Text je Kandidaten-Typ (B2 — AP ausdrücklich als „AP" gekennzeichnet).
 * Der Personen-Kandidat trägt dasselbe Label wie überall sonst: „Privat"
 * (siehe utils/contactTypeLabel.ts).
 */
export function candidateKindLabel(kind: MailSenderCandidate["kind"]): string {
    if (kind === "firma") return "Firma";
    if (kind === "ap") return "AP";
    return KONTAKTTYP_PERSON_LABEL;
}

/**
 * B1 (Review 2026-07-06) — die tatsächliche Vorgang-Zuweisung aus dem gewählten
 * Kandidaten ableiten. Ein AP-Kandidat (`kind='ap'` mit `viaCompanyId`) ist der
 * Ansprechpartner EINER FIRMA — Vorgang-Kontakt ist dann die **Firma**, nicht die
 * AP-Person. Die AP-Beziehung bleibt nur eine Info (das AP-Badge in der Leiste
 * bleibt sichtbar); die Auswahl selbst wird nicht umgebogen, nur die Zuweisung.
 * Ein reiner Person-Kandidat (ohne Firmenbezug) bleibt die Person, eine Firma
 * bleibt die Firma.
 *
 * Die Firma wird über die Kandidatenliste aufgelöst — das Backend
 * (`buildSenderCandidates`) liefert zu JEDEM AP stets die zugehörige Firma als
 * eigenen `kind='firma'`-Kandidaten mit. Fehlt sie ausnahmsweise doch, wird
 * `viaCompanyId` mit dem AP-Namen als Übergangsanzeige zugewiesen (der
 * Erstell-Flow lädt den Kontakt ohnehin per id nach — kein Datenverlust).
 */
export function resolveCandidateAssignment(
    candidate: MailSenderCandidate,
    candidates: MailSenderCandidate[],
): { id: number; displayName: string } {
    if (candidate.kind === "ap" && candidate.viaCompanyId != null) {
        const firma = candidates.find(
            (c) => c.kind === "firma" && c.contactId === candidate.viaCompanyId,
        );
        if (firma) return { id: firma.contactId, displayName: firma.displayName };
        return { id: candidate.viaCompanyId, displayName: candidate.displayName };
    }
    return { id: candidate.contactId, displayName: candidate.displayName };
}

/**
 * Leisten-Bausteine des States B in Render-Reihenfolge: die Kandidaten-Buttons,
 * dann ein VERTIKALER Trenner (eigenes Element, B3 — explizit KEIN „/"-Text),
 * dann „Neuer Vorgang".
 */
export type CommandBarItem =
    | { type: "candidate"; candidate: MailSenderCandidate }
    | { type: "combined"; ap: MailSenderCandidate; firmaName: string }
    /** T-265 — Überschrift „Aus dem Betreff:" vor dem ersten Betreff-Treffer. */
    | { type: "betreff-lead" }
    | { type: "divider" }
    | { type: "neuer-vorgang"; label: string };

/**
 * F1a: Ein AP einer Firma (`kind='ap'` mit `viaCompanyId`) wird als EIN
 * kombinierter Chip (Firma + AP) gerendert; die zugehörige Firma erscheint dann
 * NICHT mehr als eigener Chip.
 *
 * Cluster ZUWEISEN (B4): Das frühere `showReassign` (gesetzt bei
 * `apHasOwnStatus`) ist ENTFALLEN. Es steuerte einen Zusatz-Button „anders
 * zuweisen", den es nur an einem AP mit eigenem Status gab — inzwischen öffnet
 * JEDER Chip die Zuweisung zum Prüfen und Ändern.
 *
 * Mehrere AP derselben Firma erzeugen mehrere combined-Chips; die Firma wird
 * nur einmal unterdrückt (Set). Reine Firma/Person bleiben `candidate`.
 */
export function buildStateBItems(sortedCandidates: MailSenderCandidate[]): CommandBarItem[] {
    const suppressedFirmaIds = new Set(
        sortedCandidates
            .filter((c) => c.kind === "ap" && c.viaCompanyId != null)
            .map((c) => c.viaCompanyId as number),
    );
    const items: CommandBarItem[] = [];
    let betreffLead = false;
    for (const candidate of sortedCandidates) {
        if (candidate.kind === "firma" && suppressedFirmaIds.has(candidate.contactId)) {
            continue; // durch den kombinierten Chip ersetzt
        }
        if (istBetreffTreffer(candidate) && !betreffLead) {
            items.push({ type: "betreff-lead" });
            betreffLead = true;
        }
        if (candidate.kind === "ap" && candidate.viaCompanyId != null) {
            const firmaName =
                candidate.viaCompanyName ??
                sortedCandidates.find(
                    (c) => c.kind === "firma" && c.contactId === candidate.viaCompanyId,
                )?.displayName ??
                "Firma";
            items.push({ type: "combined", ap: candidate, firmaName });
            continue;
        }
        items.push({ type: "candidate", candidate });
    }
    return [...items, { type: "divider" }, { type: "neuer-vorgang", label: "Neuer Vorgang" }];
}

/** Ein Chip der Leiste — ein einzelner Kandidat oder Firma + AP kombiniert. */
export type KandidatenChip = Extract<CommandBarItem, { type: "candidate" } | { type: "combined" }>;

/** Die `contactId`, unter der ein Chip ausgewählt ist (beim kombinierten: der AP). */
export const chipKontaktId = (chip: KandidatenChip): number =>
    chip.type === "combined" ? chip.ap.contactId : chip.candidate.contactId;

/**
 * 01.11.2026 — „nicht zweimal ‚erkannt' zeigen": Der State B zeigt EINEN
 * Chip, den ausgewählten (ohne Klick: den erkannten Kontakt). Alle übrigen
 * Chips — weitere Adress-Treffer, Kunden aus dem Betreff — wandern in die
 * Auswahl „Andere", in derselben Reihenfolge wie bisher in der Leiste.
 *
 * Ohne Auswahl (`selectedId` null — nichts ist eindeutig erkannt) gibt es
 * keinen Hauptchip; dann stehen alle Chips wie bisher zur Wahl.
 */
export function teileStateB(
    sortedCandidates: MailSenderCandidate[],
    selectedId: number | null,
): { haupt: KandidatenChip | null; weitere: KandidatenChip[] } {
    const chips = buildStateBItems(sortedCandidates).filter(
        (i): i is KandidatenChip => i.type === "candidate" || i.type === "combined",
    );
    if (selectedId == null) return { haupt: null, weitere: chips };
    const haupt = chips.find((c) => chipKontaktId(c) === selectedId) ?? null;
    if (!haupt) return { haupt: null, weitere: chips };
    return { haupt, weitere: chips.filter((c) => c !== haupt) };
}

/**
 * Kurz, woher ein Kandidat stammt — Zusatz in der Auswahl „Andere", damit der
 * Nutzer sieht, warum er dort steht.
 */
export function kandidatHerkunft(c: MailSenderCandidate): string {
    if (istBetreffTreffer(c)) return "aus dem Betreff";
    if (c.matchedBy === "domain") return "über die Domäne";
    if (c.foundIn === "to") return "An";
    if (c.foundIn === "cc") return "Cc";
    return "Absender";
}

/**
 * A5 (Review 2026-07-06) — im State B („Erkannt") ist die generische
 * „Zuweisen"-Dropdown-Leiste (Kontakt/Vertrag/Risiko bzw. an Vorgang/Todo
 * anhängen) redundant: die Kandidaten-Buttons und „Neuer Vorgang" decken den
 * Zuweisungs-Flow dort bereits ab. Sie wird deshalb NUR außerhalb von B
 * angezeigt (State C sowie die neutrale Ansicht, solange der Link-Lookup noch
 * lädt). Die „Archivieren"- und Antworten-Aktionen bleiben in allen States
 * erhalten.
 */
export function showZuweisenMenu(kind: MailCommandState["kind"]): boolean {
    // Zustand D verhält sich hier wie C: es gibt keinen erkannten Kontakt, also
    // bleibt das generische Menü sichtbar.
    return kind !== "B";
}

/**
 * State A, [Bestätigen]: Ziel-Vorgänge der Zuordnung. Nimmt die vom User
 * angehakten Vorgang-ids (bei EINEM Vorgang ist das genau dieser), dedupliziert
 * sie und entfernt Vorgänge, an die DIESE Nachricht bereits gebunden ist
 * (L6 — Client-Dedupe auf messageId+vorgangId, auch über Todo-Links).
 */
export function buildConfirmTargets(
    checkedVorgangIds: number[],
    links: ConversationLinkLike[],
    messageId: string,
    internetMessageId?: string | null,
): number[] {
    const alreadyLinked = new Set(
        links
            .filter((l) => istDieseNachricht(l, messageId, internetMessageId) && l.vorgang)
            .map((l) => l.vorgang!.id),
    );
    const out: number[] = [];
    const seen = new Set<number>();
    for (const id of checkedVorgangIds) {
        if (seen.has(id) || alreadyLinked.has(id)) continue;
        seen.add(id);
        out.push(id);
    }
    return out;
}

/**
 * Payloads für `POST /mail/links` beim Bestätigen: je Ziel-Vorgang EIN Link
 * mit identischem Mail-Snapshot (derselbe Schreibpfad wie „An Bestehendes
 * anhängen"). Mehrfach-Auswahl ⇒ n Links.
 */
export function buildConfirmLinkInputs<S extends object>(
    snapshot: S,
    vorgangIds: number[],
): Array<S & { vorgangId: number }> {
    return vorgangIds.map((vorgangId) => ({ ...snapshot, vorgangId }));
}

/**
 * Defensiv-Fallback für ALT-gecachte Listenobjekte (SWR-localStorage von vor
 * SP1): trägt eine Mail noch KEIN `senderCandidates`-Feld (undefined ≠ leeres
 * Array!), aber einen erkannten Kontakt, wird dieser als Person-Kandidat
 * angezeigt statt fälschlich „Absender wurde nicht erkannt" zu warnen.
 * Ein LEERES Array bleibt leer — das ist eine echte „nichts erkannt"-Aussage
 * des Backends.
 *
 * WICHTIG (Cluster ERKENNUNG, A2): Das zweite Argument ist der
 * `recognizedContact` — NICHT mehr der `matchedContact`. Mit letzterem käme
 * über alt-gecachte Listenobjekte ohne `senderCandidates` weiterhin ein
 * interner Kollege als Chip durch, und A2 wäre über die Hintertür unterlaufen.
 */
export function effectiveSenderCandidates(
    senderCandidates: MailSenderCandidate[] | undefined,
    recognizedContact: { id: number; displayName: string } | null | undefined,
): MailSenderCandidate[] {
    if (senderCandidates !== undefined) return senderCandidates;
    if (recognizedContact) {
        return [
            {
                contactId: recognizedContact.id,
                displayName: recognizedContact.displayName,
                kind: "person",
            },
        ];
    }
    return [];
}
